import { Megaphone, ExternalLink, TrendingUp, Calendar, DollarSign } from 'lucide-react'

export default function FacebookAdsPanel({ facebookData }) {
  if (!facebookData) return null

  const { ads = [], total = 0, keyword = '', countries = [], source = '' } = facebookData

  if (ads.length === 0) return null

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch { return dateStr }
  }

  const formatSpend = (lower, upper, currency = 'USD') => {
    if (!lower && !upper) return null
    const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' '
    if (lower && upper) return `${sym}${lower}–${sym}${upper}`
    return `${sym}${lower || upper}`
  }

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <Megaphone className="w-5 h-5 text-slate-lighter" />
        <div>
          <h3 className="text-lg font-bold text-white">Facebook Ads Library</h3>
          <p className="text-xs text-slate-light">
            Anuncios activos de la competencia · {source === 'rapidapi' ? 'vía RapidAPI' : 'API oficial de Meta'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 bg-slate-darker border border-slate-dark/40 text-slate-lighter rounded">
            {total} anuncios
          </span>
          {countries.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {countries.slice(0, 3).map(c => (
                <span key={c} className="text-xs px-2 py-0.5 bg-slate-darker border border-slate-dark/40 text-slate-lighter rounded">{c}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ads list */}
      <div className="space-y-3">
        {ads.slice(0, 8).map((ad, idx) => {
          const spend = formatSpend(ad.spend_lower, ad.spend_upper, ad.currency)
          return (
            <div
              key={ad.id || idx}
              className="p-4 bg-slate-darker border border-slate-dark/20 rounded-lg hover:border-slate-dark/50 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Avatar + nombre de página */}
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-8 h-8 rounded-full bg-slate-dark flex items-center justify-center">
                    <span className="text-sm font-bold text-slate-lighter">
                      {(ad.page_name || 'A').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-lightest">{ad.page_name || 'Anunciante'}</p>
                    {ad.is_active !== undefined && (
                      <span className={`text-xs ${ad.is_active ? 'text-green-400' : 'text-slate-light'}`}>
                        {ad.is_active ? '● Activo' : '○ Inactivo'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Fecha + gasto + link */}
                <div className="flex items-center gap-2 shrink-0 ml-auto">
                  {ad.created_at && (
                    <span className="text-xs text-slate-light flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(ad.created_at)}
                    </span>
                  )}
                  {spend && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      {spend}
                    </span>
                  )}
                  {ad.snapshot_url && (
                    <a
                      href={ad.snapshot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-light hover:text-slate-lighter transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>

              {ad.image && (
                <img src={ad.image} alt="" loading="lazy" referrerPolicy="no-referrer"
                  className="mt-3 w-full max-h-48 object-contain rounded bg-black/30"
                  onError={(e) => { e.currentTarget.style.display = 'none' }} />
              )}

              {/* Título del anuncio */}
              {ad.title && (
                <p className="mt-2 text-sm font-medium text-slate-lightest">{ad.title}</p>
              )}

              {/* Cuerpo del anuncio */}
              {ad.body && (
                <p className="mt-1 text-xs text-slate-300 line-clamp-3 leading-relaxed">{ad.body}</p>
              )}

              {/* CTA + plataformas */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {ad.cta && (
                  <span className="text-xs px-2 py-0.5 bg-blue-900/30 border border-blue-700/30 text-blue-300 rounded">{ad.cta}</span>
                )}
                {(ad.platforms || []).map(p => (
                  <span key={p} className="text-xs px-2 py-0.5 bg-slate-dark border border-slate-dark/40 text-slate-light rounded capitalize">{p}</span>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer insight */}
      <div className="mt-4 p-3 bg-slate-darker rounded-lg border border-slate-dark/20">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-slate-light shrink-0" />
          <p className="text-xs text-slate-300">
            {total > 20
              ? `Alta actividad publicitaria: ${total}+ anuncios detectados. La competencia invierte fuerte en Facebook Ads.`
              : total > 5
              ? `Competencia moderada: ${total} anuncios activos. Hay espacio para diferenciarse.`
              : `Poca competencia en ads (${total} anuncios). Oportunidad de posicionamiento con bajo costo.`}
          </p>
        </div>
      </div>
    </div>
  )
}
